import type { DiscoverFilterOptions, DiscoverFilters } from "~/react/types/discover"
import { Icon } from "~/react/ui/icon"
import { Button } from "~/react/ui/button"
import {
  joinList,
  formatIncludeExclude,
  mapCompanyTypeLabels,
  mapIndustryLabels,
  mapTechnologyLabels,
  formatFundingSeries,
  formatFundingAmount,
  formatFundingDate,
} from "./compact_filters_helpers"

const t = (key: string) => window.i18n.t(key)

// Ensure value is always an array (handles objects from malformed filter data)
function ensureArray(value: unknown): string[] {
  if (value === undefined || value === null) return []
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (Array.isArray(item)) return String(item[0])
      return String(item)
    })
  }
  if (typeof value === "object") return Object.keys(value)
  return [String(value)]
}

interface DiscoverCompactFilterProps {
  filters: DiscoverFilters
  filterOptions: DiscoverFilterOptions
  onDisableCompactView: () => void
}

interface SummaryRow {
  id: string
  icon: string
  label: string
  value: string
  included?: string
  excluded?: string
  operator?: "and" | "or"
}

function getLocationRow(filters: DiscoverFilters, filterOptions: DiscoverFilterOptions): SummaryRow | null {
  const countryNames = filterOptions.country_names || {}
  const stateNames = filterOptions.state_names || {}

  // Convert codes to display names
  const toName = (code: string, type: "country" | "state" | "other"): string => {
    if (type === "country") return countryNames[code] || code
    if (type === "state") return stateNames[code] || code
    return code
  }

  const includedLocations = [
    ...ensureArray(filters.location_continent_included),
    ...ensureArray(filters.location_business_region_included),
    ...ensureArray(filters.location_country_included).map((c) => toName(c, "country")),
    ...ensureArray(filters.location_state_included).map((s) => toName(s, "state")),
    ...ensureArray(filters.location_city_included),
  ]

  const excludedLocations = [
    ...ensureArray(filters.location_continent_excluded),
    ...ensureArray(filters.location_business_region_excluded),
    ...ensureArray(filters.location_country_excluded).map((c) => toName(c, "country")),
    ...ensureArray(filters.location_state_excluded).map((s) => toName(s, "state")),
    ...ensureArray(filters.location_city_excluded),
  ]

  const locationValue = formatIncludeExclude(includedLocations, excludedLocations)

  if (!locationValue) return null

  return {
    id: "location",
    icon: "map-location-dot",
    label: t("js.back.discover.filters.location.title"),
    value: locationValue,
    included: includedLocations.length > 0 ? includedLocations.join(", ") : undefined,
    excluded: excludedLocations.length > 0 ? excludedLocations.join(", ") : undefined,
  }
}

function getIndustryRow(filters: DiscoverFilters, filterOptions: DiscoverFilterOptions): SummaryRow | null {
  const industryIncluded = mapIndustryLabels(filters.industry_included, filterOptions.industries)
  const industryExcluded = mapIndustryLabels(filters.industry_excluded, filterOptions.industries)

  const industryValue = formatIncludeExclude(industryIncluded, industryExcluded)

  if (!industryValue) return null

  return {
    id: "industry",
    icon: "buildings",
    label: t("js.back.discover.filters.industry.title"),
    value: industryValue,
    included: industryIncluded.length > 0 ? industryIncluded.join(", ") : undefined,
    excluded: industryExcluded.length > 0 ? industryExcluded.join(", ") : undefined,
  }
}

function getKeywordsRow(filters: DiscoverFilters): SummaryRow | null {
  const keywordsValue = formatIncludeExclude(filters.keywords_included, filters.keywords_excluded)

  if (!keywordsValue) return null

  const totalKeywordsCount = (filters.keywords_included?.length || 0) + (filters.keywords_excluded?.length || 0)
  const showOperator = totalKeywordsCount > 1 && filters.keywords_operator

  return {
    id: "keywords",
    icon: "hashtag",
    label: t("js.back.discover.filters.keywords.title"),
    value: keywordsValue,
    included:
      filters.keywords_included && filters.keywords_included.length > 0
        ? filters.keywords_included.join(", ")
        : undefined,
    excluded:
      filters.keywords_excluded && filters.keywords_excluded.length > 0
        ? filters.keywords_excluded.join(", ")
        : undefined,
    operator: showOperator ? filters.keywords_operator : undefined,
  }
}

function getSizeRow(filters: DiscoverFilters): SummaryRow | null {
  const sizeValue = joinList(filters.company_size)

  if (!sizeValue) return null

  return {
    id: "size",
    icon: "users",
    label: t("js.back.discover.filters.company_size.title"),
    value: sizeValue,
  }
}

function getCompanyNameRow(filters: DiscoverFilters): SummaryRow | null {
  const companyNameValue = joinList(filters.domains)

  if (!companyNameValue) return null

  return {
    id: "company_name",
    icon: "building",
    label: t("js.back.discover.filters.company_name.title"),
    value: companyNameValue,
  }
}

function getSimilarCompaniesRow(filters: DiscoverFilters): SummaryRow | null {
  if (!filters.similar_to_domain) return null

  return {
    id: "similar_companies",
    icon: "magnifying-glass",
    label: t("js.back.discover.filters.similar_companies.title"),
    value: filters.similar_to_domain,
  }
}

function getCompanyTypeRow(filters: DiscoverFilters, filterOptions: DiscoverFilterOptions): SummaryRow | null {
  const companyTypeIncluded = mapCompanyTypeLabels(filters.company_type_included, filterOptions.company_types)
  const companyTypeExcluded = mapCompanyTypeLabels(filters.company_type_excluded, filterOptions.company_types)

  const companyTypeValue = formatIncludeExclude(companyTypeIncluded, companyTypeExcluded)

  if (!companyTypeValue) return null

  return {
    id: "company_type",
    icon: "sitemap",
    label: t("js.back.discover.filters.company_type.title"),
    value: companyTypeValue,
    included: companyTypeIncluded.length > 0 ? companyTypeIncluded.join(", ") : undefined,
    excluded: companyTypeExcluded.length > 0 ? companyTypeExcluded.join(", ") : undefined,
  }
}

function getFoundedYearRow(filters: DiscoverFilters): SummaryRow | null {
  const foundedYearIncluded = filters.founded_year_included || []
  const foundedYearExcluded = filters.founded_year_excluded || []
  let foundedValue = ""

  if (filters.founded_year_from && filters.founded_year_to) {
    if (filters.founded_year_from === filters.founded_year_to) {
      foundedValue = String(filters.founded_year_from)
    } else {
      const fromLabel = t("js.back.discover.filters.from")
      const toLabel = t("js.back.discover.filters.to")
      foundedValue = `${fromLabel} ${filters.founded_year_from} – ${toLabel} ${filters.founded_year_to}`
    }
  } else if (filters.founded_year_from) {
    const fromLabel = t("js.back.discover.filters.from")
    foundedValue = `${fromLabel} ${filters.founded_year_from}`
  } else if (filters.founded_year_to) {
    const toLabel = t("js.back.discover.filters.to")
    foundedValue = `${toLabel} ${filters.founded_year_to}`
  } else if (foundedYearIncluded.length > 0 || foundedYearExcluded.length > 0) {
    const includedText = foundedYearIncluded.length > 0 ? foundedYearIncluded.join(", ") : undefined
    const excludedText = foundedYearExcluded.length > 0 ? foundedYearExcluded.join(", ") : undefined
    foundedValue = formatIncludeExclude(
      includedText ? [includedText] : undefined,
      excludedText ? [excludedText] : undefined,
    )
  }

  if (!foundedValue) return null

  return {
    id: "founded_year",
    icon: "calendar",
    label: t("js.back.discover.filters.founded_year.title"),
    value: foundedValue,
    included: foundedYearIncluded.length > 0 ? foundedYearIncluded.join(", ") : undefined,
    excluded: foundedYearExcluded.length > 0 ? foundedYearExcluded.join(", ") : undefined,
  }
}

function getTechnologiesRow(filters: DiscoverFilters, filterOptions: DiscoverFilterOptions): SummaryRow | null {
  const technologyIncluded = mapTechnologyLabels(filters.technology_included, filterOptions.technologies)
  const technologyExcluded = mapTechnologyLabels(filters.technology_excluded, filterOptions.technologies)

  const technologyValue = formatIncludeExclude(technologyIncluded, technologyExcluded)

  if (!technologyValue) return null

  const totalTechnologiesCount = technologyIncluded.length + technologyExcluded.length
  const showOperator = totalTechnologiesCount > 1 && filters.technology_operator

  return {
    id: "technologies",
    icon: "microchip",
    label: t("js.back.discover.filters.technologies.title"),
    value: technologyValue,
    included: technologyIncluded.length > 0 ? technologyIncluded.join(", ") : undefined,
    excluded: technologyExcluded.length > 0 ? technologyExcluded.join(", ") : undefined,
    operator: showOperator ? filters.technology_operator : undefined,
  }
}

function getFundingsRow(filters: DiscoverFilters): SummaryRow | null {
  const fundingParts: string[] = []

  if (filters.funding_series && filters.funding_series.length > 0) {
    fundingParts.push(formatFundingSeries(filters.funding_series))
  }

  const fundingAmount = formatFundingAmount(filters.funding_amount_from, filters.funding_amount_to)
  if (fundingAmount) {
    fundingParts.push(fundingAmount)
  }

  const fundingDate = formatFundingDate(filters.funding_date, filters.funding_date_from, filters.funding_date_to)
  if (fundingDate) {
    fundingParts.push(fundingDate)
  }

  if (fundingParts.length === 0) return null

  return {
    id: "fundings",
    icon: "hand-holding-dollar",
    label: t("js.back.discover.filters.funding.title"),
    value: fundingParts.join(", "),
  }
}

function buildSummaryRows(filters: DiscoverFilters, filterOptions: DiscoverFilterOptions): SummaryRow[] {
  const rowBuilders = [
    () => getLocationRow(filters, filterOptions),
    () => getIndustryRow(filters, filterOptions),
    () => getKeywordsRow(filters),
    () => getSizeRow(filters),
    () => getCompanyNameRow(filters),
    () => getSimilarCompaniesRow(filters),
    () => getCompanyTypeRow(filters, filterOptions),
    () => getFoundedYearRow(filters),
    () => getTechnologiesRow(filters, filterOptions),
    () => getFundingsRow(filters),
  ]

  return rowBuilders.map((builder) => builder()).filter((row): row is SummaryRow => row !== null)
}

interface CompactFilterSummaryProps {
  row: SummaryRow
}

function CompactFilterSummary({ row }: CompactFilterSummaryProps) {
  return (
    <div className="discover-compact-filter__row">
      <Icon icon={row.icon} aria-hidden="true" size="sm" className="discover-compact-filter__icon" />
      <div className="discover-compact-filter__content">
        <span className="discover-compact-filter__label">{row.label}</span>
        <span className="discover-compact-filter__value">
          {row.operator && (
            <>
              <span className="discover-compact-filter__operator">
                {row.operator === "and"
                  ? t("js.back.discover.filters.match_all")
                  : t("js.back.discover.filters.match_any")}
              </span>
              {": "}
            </>
          )}
          {row.included && row.excluded ? (
            <>
              {row.included},{" "}
              <span className="discover-compact-filter__exclude-prefix">
                {t("js.back.discover.compact.exclude_prefix")}
              </span>{" "}
              {row.excluded}
            </>
          ) : row.excluded ? (
            <>
              <span className="discover-compact-filter__exclude-prefix">
                {t("js.back.discover.compact.exclude_prefix")}
              </span>{" "}
              {row.excluded}
            </>
          ) : (
            row.value
          )}
        </span>
      </div>
    </div>
  )
}

export function DiscoverCompactFilter({ filters, filterOptions, onDisableCompactView }: DiscoverCompactFilterProps) {
  const rows = buildSummaryRows(filters, filterOptions)

  if (rows.length === 0) {
    return null
  }

  return (
    <div className="discover-compact-filter">
      <div className="discover-compact-filter__rows">
        {rows.map((row) => (
          <CompactFilterSummary key={row.id} row={row} />
        ))}
      </div>

      <div className="discover-compact-filter__footer">
        <span className="discover-compact-filter__status">
          {t("js.back.discover.compact.compact_view_enabled")}
          {" · "}
        </span>
        <Button
          type="button"
          variant="linkGrey"
          size="xs"
          className="discover-compact-filter__toggle"
          onClick={onDisableCompactView}
        >
          {t("js.back.discover.compact.disable")}
        </Button>
      </div>
    </div>
  )
}
